/**
 * 작성자 : 한영광
 * 날짜 : 2024.10.31
 * 기능 : 마스터일반관리 - 사내관리 - 사내정보탭
 */

import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { userState } from '../../../atoms';
import { IComCdList, IModal } from 'customTypes';
import { COMCD_JONGMOK, COMCD_UPTAE, COMCD_USE_YN, INIT_CCTV_YN, INIT_USE_YN_A, INIT_USE_YN_Y } from '../../../_constants';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { BtnGhost } from '../../../components/Button';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import ViewTable from '../../../components/Table/ViewTable';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import SelectBox from '../../../components/SelectBox';
import { trimObject } from '../../../utils/trimObject';
import { setComCdListState } from '../../../utils/setComCdListState';
import { logPost } from '../../../services/log';
import BackButton from '../../../components/BackButton';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import CompanyInfoInputForm from '../../../components/Form/CompanyInfoInputForm';

const Root = styled.div`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .inputForm-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  > .inputFormsWrapper {
    height: 3.5rem !important;
    flex-shrink: 0;
    display: flex;
    flex-direction: row !important;
    align-items: center;
    padding: 0 0.5rem;
    border-bottom: 1px solid var(--outline);
    @media (min-width: 1025px) {
      padding: 0 1rem;
    }
  }
`;

type IsUpdatedLogoImage = 'N' | 'U' | 'D';
type Logo = { light: string; dark: string };

const CompanyInfo = ({ tabType }: { tabType: string }) => {
  const { t } = useTranslation();
  const INIT_UPTAE = { type: COMCD_UPTAE, [COMCD_UPTAE]: '', cdName: t('미선택') };
  const INIT_JONGMOK = { type: COMCD_JONGMOK, [COMCD_JONGMOK]: '', cdName: t('미선택') };
  const size = useOutletContext<any>();

  const userInfo = useRecoilValue(userState);
  const node = useRef<any>(null);
  const userInfoInputFormRef = useRef<any>(null); // 인풋 폼 ref
  const [searchOptionCName, setSearchOptionCName] = useState({ cName: '' });
  const [searchOptionUseYn, setSearchOptionUseYn] = useState(INIT_USE_YN_A);
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [rowState, setRowState] = useState({
    address1: '',
    address2: '',
    bigo: '',
    delYn: '',
    eDate: '',
    editor: '',
    cCd: '',
    cCeoname: '',
    cLogoimage: '',
    cdLogoimage: '',
    cName: '',
    cNum: '',
    cTel: '',
    jongmok: '',
    postNum: '',
    subDomain: '',
    uptae: '',
    useYn: 'Y',
    cctvYn: 'Y',
    wDate: null,
    writer: null,
    latitude: '',
    longitude: '',
  }); // 서버에서 받아 온 raw data 어레이 중 1줄의 데이터 state
  const [viewData, setViewData] = useState([{ cCd: '', cName: '', useYn: '' }]);
  // 서버에서 받아 온 raw data 중 viewTable에 뿌려줄 데이터만 담는 state
  const [columnCount, setColumnCount] = useState(0);
  const [masterCompanyList, setMasterCompanyList] = useState<any>([]);
  const [orgMasterCompanyList, setOrgMasterCompanyList] = useState<any>([]); // 필터링되지않은 본사리스트
  const [uptaeComCdList, setUptaeComCdList] = useState<IComCdList[]>([]); // 업태 공통코드
  const [jongmokComCdList, setJongmokComCdList] = useState<IComCdList[]>([]); // 종목 공통코드
  const [cTelState, setCTelState] = useState({ num1: '', num2: '', num3: '' });
  const [cNumState, setCNumState] = useState({ num1: '', num2: '', num3: '' });
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });

  const initLogo = { light: '', dark: '' };
  const [logoImage, setLogoImage] = useState<Logo>(initLogo);
  const [typeOfLogoImage, setTypeOfLogoImage] = useState<Logo>(initLogo);
  // 로고 이미지 상태(D: 삭제, U: 수정, N: 변경없음)
  const [isUpdatedLogoImage, setIsUpdatedLogoImage] = useState<{ light: IsUpdatedLogoImage; dark: IsUpdatedLogoImage }>({ light: 'N', dark: 'N' });

  const [uptae, setUptae] = useState(INIT_UPTAE);
  const [jongmok, setJongmok] = useState(INIT_JONGMOK);
  const [useYn, setUseYn] = useState(INIT_USE_YN_Y);
  const [cctvYn, setCctvYn] = useState(INIT_CCTV_YN); // cctv 모니터링여부 yn

  // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [viewMainPage, setViewMainPage] = useState(true);
  // 사용여부 공통코드 목록 (검색조건에서 사용, 전체포함)
  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true);
  // 사용유무 공통코드 목록 (사용자 정보에서 사용)
  const { data: useYnComCdList } = useFetchCommonCodeList(COMCD_USE_YN, false);

  // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
  const randomString = Math.random().toString(36).substring(7);

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '사내 관리 > 사내 정보',
      action: '조회',
      etc: ``,
    });
  }, []);

  // 본사 정보를 불러오는 API 호출 후,
  // 성공적으로 데이터를 받아온 경우 사용유무 검색 옵션을 초기화합니다.
  useEffect(() => {
    const getHeadList = () => {
      getHeadListAPI().then((res: any) => {
        // API 호출 결과가 성공(200)인 경우
        if (res.status === 200) {
          // 사용유무 검색 옵션을 '전체'로 설정합니다.
          setSearchOptionUseYn(INIT_USE_YN_A);
        }
      });
    };
    getHeadList();
    // viewMainPage 상태가 변경될 때마다 이 useEffect를 다시 실행합니다.
  }, [viewMainPage, rowState]);

  useEffect(() => {
    // 업태값이 변경되면 종목 api 호출 및 종목선택값 초기화
    if (isNewAdd || uptae[COMCD_UPTAE] !== '') {
      getJongmokListAPI(uptae[COMCD_UPTAE]);
    }
  }, [uptae[COMCD_UPTAE], viewMainPage, isNewAdd]);

  useEffect(() => {
    // 본사 선택 시 로고 이미지 상태를 업데이트합니다.
    setLogoImage({
      // 로고 이미지가 있으면 사용하고, 없으면 빈 문자열을 할당합니다.
      light: rowState.cLogoimage ? `${rowState.cLogoimage}?v=${randomString}` : '',
      dark: rowState.cdLogoimage ? `${rowState.cdLogoimage}?v=${randomString}` : '',
    });
    // }, [rowState.hCd, viewMainPage, rowState.hLogoimage, rowState.hdLogoimage]);
  }, [rowState.cCd, viewMainPage]);

  useEffect(() => {
    // 도메인에 따라서 뷰테이블에 전달해야하는 키값만 viewTableProperties 배열안에 작성하면 됨.
    if (masterCompanyList && masterCompanyList.length > 0) {
      const viewTableProperties = ['cCd', 'cName', 'useYn'];
      setDataIntoViewTable(viewTableProperties);
    }
  }, [masterCompanyList, viewMainPage]);

  useEffect(() => {
    // 검색옵션 변경됐을 때 필터링 처리
    // 필터링 기준
    const filterOptions = {
      cName: searchOptionCName.cName,
    };

    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = orgMasterCompanyList.filter((item: any) => {
      return item.cName?.toLowerCase()?.includes(filterOptions.cName?.toLowerCase());
    });

    const useYnFilter = YNFilter(filteredArray, 'useYn', searchOptionUseYn.USE_YN);

    if (useYnFilter.length > 0) {
      setMasterCompanyList(useYnFilter);
    } else {
      setMasterCompanyList([]);
    }
  }, [searchOptionCName, searchOptionUseYn.USE_YN, orgMasterCompanyList, viewMainPage]);

  const YNFilter = (initialArray: any[], key: string, option: any) => {
    if (option === 'Y') {
      return initialArray.filter((el: any) => el[key] === 'Y');
    }
    if (option === 'N') {
      return initialArray.filter((el: any) => el[key] === 'N');
    }
    return initialArray;
  };

  // 로우데이터에서 뷰테이블에 보여줄 데이터만 뽑아서 배열에 담아 리턴
  const setDataIntoViewTable = (properties: any) => {
    setColumnCount(properties.length);
    const viewDataArray: any = [];
    const allKeys = Object.keys(masterCompanyList[0]);

    masterCompanyList?.map((el: any, i: number) => {
      const result = allKeys.reduce((next, key: any) => {
        if (properties.includes(key)) {
          return { ...next, [key]: masterCompanyList[i][key] };
        }
        return next;
      }, {});
      return viewDataArray.push(result);
    });
    setViewData(viewDataArray);
  };

  // 삭제버튼 클릭으로 삭제 모달창 띄우기
  const onClickDelete = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteHeadAPI }));
  };

  // 사내 정보 조회 API
  const getHeadListAPI = async () => {
    const res = await apiGet({ path: '/company/info' });
    // const res = await apiGet({ path: '/head' });
    const { status, data } = res;
    if (status === 200) {
      // 삭제되지 않은 본사만 필터링해서 setState
      setComCdListState(COMCD_UPTAE, setUptaeComCdList, false); // 공통코드(업태) 리스트 조회 API
      // const activeHeadInfos = data.data.headInfoList.filter((el: any) => el.delYn === 'N');
      setMasterCompanyList(data.data);
      setOrgMasterCompanyList(data.data);
    }
    return res;
  };

  // 공통코드(종목) 리스트 조회 API
  const getJongmokListAPI = async (uptaeCode: string) => {
    const req = { grCd: COMCD_JONGMOK };
    const res = await apiGet({ path: '/code/detail', req });
    const { status, data } = res;
    if (status === 200) {
      const subJongmok = data.data.comCdList.filter((el: any) => el.etc1 === uptaeCode);
      const newArray = subJongmok.map((el: any) => ({ type: COMCD_JONGMOK, [COMCD_JONGMOK]: el.subCd, cdName: el.cdName }));
      setJongmokComCdList(newArray);
    }
  };

  // 본사 정보를 저장하거나 수정하는 API 함수
  const postCompanyAPI = async (reqData: any) => {
    // reqData에서 필요한 데이터를 구조 분해 할당으로 추출
    const { address1, address2, bigo, delYn, cCd, cCeoname, cLogoimage, cdLogoimage, cName, cNum, cTel, postNum, subDomain, latitude, longitude, logoBase64, logoBase64D } = reqData;
    // API 요청에 필요한 데이터를 req 객체에 할당
    const req = {
      address1,
      address2,
      bigo,
      delYn,
      cCd,
      cCeoname,
      cLogoimage, // 라이트모드 로고
      cdLogoimage, // 다크모드 로고
      cName,
      cNum,
      cTel,
      postNum,
      subDomain,
      uptae: uptae[COMCD_UPTAE], // 업태 코드
      jongmok: jongmok[COMCD_JONGMOK], // 종목 코드
      useYn: useYn[COMCD_USE_YN], // 사용 여부
      // cctvYn: cctvYn.cctvYn, // cctv 모니터링 사용유무
      latitude,
      longitude,
      logoBase64, // 라이트모드 로고 Base64
      logoBase64D, // 다크모드 로고 Base64
      logoType: reqData.logoType, // 라이트모드 로고 타입
      logoTypeD: reqData.logoTypeD, // 다크모드 로고 타입
      // editor: userInfo.userId, // 수정자 정보
      // ...(reqData.cCd && { writer: userInfo.userId }), // hCd가 있으면 작성자 정보 추가
    };

    // API POST 요청 실행
    const res = await apiPost({ path: '/company/info', req });
    // const res = await apiPost({ path: '/head', contentType: 'multipart/form-data', req });
    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      const findData = isNewAdd ? req : data.find((el: any) => el.cCd === rowState.cCd);

      // 새로운 사내 정보를 상태에 반영
      const newRowState = {
        ...findData,
        cLogoimage: findData.cLogoimage !== '' ? `${data.cLogoimage}?v=${randomString}` : '',
        cdLogoimage: findData.cdLogoimage !== '' ? `${data.cdLogoimage}?v=${randomString}` : '',
      };

      if (isNewAdd) {
        initiateState();
        setIsNewAdd(true);
        setUseYn(INIT_USE_YN_Y);
        setCctvYn(INIT_CCTV_YN);
        setUptae(INIT_UPTAE);
        setJongmok(INIT_JONGMOK);
      } else {
        setRowState(findData);
      }
      // 업태와 종목 상태 업데이트
      setUptae({ type: COMCD_UPTAE, [COMCD_UPTAE]: findData.uptae, cdName: findData.uptaeName });
      setJongmok({ type: COMCD_JONGMOK, [COMCD_JONGMOK]: findData.jongmok || '', cdName: findData.jongmokName || t('미선택') });

      // 사내 정보 목록을 업데이트
      const newMasterCompanyList = [];
      if (!req.cCd) {
        // 신규 사내 정보인 경우
        newMasterCompanyList.push(...masterCompanyList, newRowState);
      } else {
        // 기존 사내 정보 수정인 경우
        for (let v of masterCompanyList) {
          if (v.hCd === newRowState.cCd) {
            v = newRowState;
          }
          newMasterCompanyList.push(v);
        }
      }
      // 상태 업데이트
      setMasterCompanyList(newMasterCompanyList);
      setOrgMasterCompanyList(newMasterCompanyList);
      // 성공 알림
      toast.success(t('사내 정보 저장 성공'));
      // 로그 기록
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '마스터일반관리-사내관리',
        action: '사내 정보 저장&수정',
        etc: `${reqData.cName}(${reqData.cCd || '신규'})`,
      });
    } else {
      // 요청 실패 시 에러 처리
      // toast.error(t(ERROR));
      toast.error(t('사내 정보 저장 실패'));
    }
  };

  // 본사정보 삭제 API
  const deleteHeadAPI = async () => {
    const req = { cCd: rowState.cCd };
    const res = await apiDelete({ path: '/company/info', req });
    const { status, data } = res;
    if (status === 200) {
      const newMasterCompanyList = [];
      for (const v of masterCompanyList) {
        if (v.cCd !== req.cCd) {
          newMasterCompanyList.push(v);
        }
      }
      setMasterCompanyList(newMasterCompanyList);
      setOrgMasterCompanyList(newMasterCompanyList);
      initiateState();
      setIsNewAdd(true);
      setUseYn(INIT_USE_YN_Y);
      setCctvYn(INIT_CCTV_YN);
      setUptae(INIT_UPTAE);
      setJongmok(INIT_JONGMOK);
      toast.success(t('사내 정보 삭제 완료'));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '마스터일반관리-사내관리',
        action: '사내 정보 삭제',
        etc: `${rowState.cName}(${rowState.cCd})`,
      });
    }
  };

  /**
   * 저장 버튼 클릭 이벤트 핸들러입니다.
   * 입력된 데이터를 검증한 후 API를 호출하여 데이터를 저장합니다.
   */
  const onClickSave = () => {
    // 저장 버튼 클릭 상태를 true로 설정합니다.
    setIsSaveClicked(true);
    // 필수 입력값 목록을 정의합니다.
    const requireList = [uptae[COMCD_UPTAE], rowState.cName];
    // 필수 입력값이 모두 입력되었는지 검증합니다.
    for (const row of requireList) {
      if (row === '') {
        // 필수 입력값이 누락된 경우 경고 메시지를 표시하고 함수를 종료합니다.
        return toast.warning(t('필수입력값을 모두 입력하세요'));
      }
    }
    // 전화번호와 사업자 번호를 '-'를 사용하여 병합합니다.
    const cTelMergedNumber = `${cTelState.num1}-${cTelState.num2}-${cTelState.num3}`;
    const cNumMergedNumber = `${cNumState.num1}-${cNumState.num2}-${cNumState.num3}`;

    // 로고 업데이트 상태에 따라 로고 이미지의 Base64 인코딩 값을 설정합니다.
    const getLogoBase64 = (logoState: string, isUpdateState: string) => {
      if (isUpdateState === 'U') {
        // 로고가 업데이트된 경우 Base64 인코딩 값을 추출합니다.
        return logoState.split(',')[1] || '';
      }
      // 로고가 업데이트된 경우가 아니라면 모든 로고 관련 값을 초기화합니다.
      return '';
    };

    // 현재 상태에서 로고 이미지 정보를 추출합니다.
    let { cLogoimage, cdLogoimage } = rowState;

    // 라이트모드/다크모드의 Base64 인코딩 값을 설정합니다.
    const logoBase64 = getLogoBase64(logoImage.light, isUpdatedLogoImage.light);
    if (isUpdatedLogoImage.light === 'D') {
      cLogoimage = '';
    }
    const logoBase64D = getLogoBase64(logoImage.dark, isUpdatedLogoImage.dark);
    if (isUpdatedLogoImage.dark === 'D') {
      cdLogoimage = '';
    }

    // API 요청 데이터를 준비합니다.
    const reqData = {
      ...rowState,
      cTel: cTelMergedNumber !== '--' ? cTelMergedNumber : '',
      cNum: cNumMergedNumber !== '--' ? cNumMergedNumber : '',
      editor: userInfo.userId,
      delYn: 'N',
      cLogoimage,
      cdLogoimage,
      logoBase64,
      logoBase64D,
      logoType: typeOfLogoImage.light,
      logoTypeD: typeOfLogoImage.dark,
    };
    // 요청 데이터에서 불필요한 공백을 제거합니다.
    const trimData = trimObject(reqData);

    // 준비된 데이터를 사용하여 API를 호출합니다.
    return postCompanyAPI(trimData);
  };

  // 신규등록 버튼 클릭해서 새 입력창(iputForm) 노출
  const onClickNewRegistration = () => {
    initiateState();
    setIsNewAdd(true);
    setUseYn(INIT_USE_YN_Y);
    setCctvYn(INIT_CCTV_YN);
    setUptae(INIT_UPTAE);
    setJongmok(INIT_JONGMOK);
    if (size.innerSize.W <= 1024) setViewMainPage(false);
  };

  const onClickInitiateSearchOption = () => {
    setSearchOptionUseYn(INIT_USE_YN_A);
    setSearchOptionCName({ cName: '' });
    setUseYn(INIT_USE_YN_Y);
    setCctvYn(INIT_CCTV_YN);
    // onClickNewRegistration(); // 우측 인풋창도 초기화
  };

  // 스테이트 객체의 밸류를 ''로 초기화
  const initiateState = () => {
    Object.keys(rowState).map((el: any) => {
      return setRowState((prev: any) => ({ ...prev, [el]: '', useYn: 'Y' }));
    });
    setIsSaveClicked(false);
  };

  const backToMain = () => {
    setViewMainPage(true);
  };

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root className={size.innerSize.W > 1024 || viewMainPage ? 'showRoot' : 'hideRoot'}>
        <SearchOptions>
          {size.innerSize.W > 1024 ? (
            <>
              <div className='inputsWrapper'>
                <div className='inputForm-row'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='useYn'>{t('사용유무')}</label>
                    <SelectBox
                      options={useYnComCdListWithAll}
                      defaultOption={searchOptionUseYn.cdName}
                      state={searchOptionUseYn}
                      setState={setSearchOptionUseYn}
                      stateKey={COMCD_USE_YN}
                      initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                      filterbar='filter-1-left'
                    />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <Input placeholder={t('사내명')} label='' type='text' id='cName' name='cName' state={searchOptionCName} setState={setSearchOptionCName} />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  </div>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickNewRegistration}>
                      <span className='material-symbols-rounded'>add</span>
                      {t('등록')}
                    </BtnGhost>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='inputForm-row' style={{ width: '100%' }}>
                  <div className='inputForm-col'>
                    <Input placeholder={t('사내명')} label='' type='text' id='cName' name='cName' state={searchOptionCName} setState={setSearchOptionCName} ico='search' />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={onClickNewRegistration}>
                    <span className='material-symbols-rounded'>add</span>
                    {t('등록')}
                  </BtnGhost>
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        <div className='searchContent inputForm-body' ref={node}>
          <ViewTable
            style={{
              width: 'calc(50% - 2rem)',
              colCount: columnCount,
            }}
            rawData={masterCompanyList}
            state={viewData}
            setRowState={setRowState}
            setIsNewAdd={setIsNewAdd}
            userInfoInputFormRef={userInfoInputFormRef}
            setViewMainPage={() => setViewMainPage(false)}
            setUseYn={setUseYn}
            setCctvYn={setCctvYn}
            setJongmok={setJongmok}
            setUptae={setUptae}
            tabType={tabType}
          />
        </div>
      </Root>
      {(size.innerSize.W > 1024 || !viewMainPage) && (
        <Root>
          <div className='inputFormsWrapper flexRowEm'>
            {size.innerSize.W <= 1024 && <BackButton func={() => backToMain()} />}

            <div className='formTitle'>{isNewAdd ? t(`새로운 사내 정보`) : t(`사내 정보 상세`)}</div>
          </div>
          <CompanyInfoInputForm
            state={rowState}
            setState={setRowState}
            uptaeList={uptaeComCdList}
            jongmokList={jongmokComCdList}
            useYnList={useYnComCdList}
            uptae={uptae}
            setUptae={setUptae}
            jongmok={jongmok}
            setJongmok={setJongmok}
            useYn={useYn}
            setUseYn={setUseYn}
            cctvYn={cctvYn}
            setCctvYn={setCctvYn}
            cTelState={cTelState}
            setCTelState={setCTelState}
            cNumState={cNumState}
            setCNumState={setCNumState}
            isNewAdd={isNewAdd}
            onClickSave={onClickSave}
            onClickDelete={onClickDelete}
            logoImage={logoImage}
            setLogoImage={setLogoImage}
            setTypeOfLogoImage={setTypeOfLogoImage}
            setIsUpdatedLogoImage={setIsUpdatedLogoImage}
            isSaveClicked={isSaveClicked}
            isUpdatedLogoImage={isUpdatedLogoImage}
          />
        </Root>
      )}
      <Portal openModal={openModal?.status}>{openModal && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </div>
  );
};

export default CompanyInfo;
